/**
 * Reads the real sky: browser geolocation for where you are, Open-Meteo for
 * cloud cover, and the solar math for how high the sun sits.
 *
 * Open-Meteo needs no API key or account. Coordinates are rounded to ~1km
 * before leaving the device — the weather doesn't change on your street.
 */

import { classifySky, sunElevationDeg, type Sky } from '@/domain/solar'

export interface SkyReading {
  sky: Sky
  elevationDeg: number
  cloudCoverPct: number
  at: Date
}

function getPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('no geolocation'))
      return
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      timeout: 8000,
      maximumAge: 10 * 60 * 1000,
    })
  })
}

export async function readSky(): Promise<SkyReading> {
  const pos = await getPosition()
  const lat = Math.round(pos.coords.latitude * 100) / 100
  const lon = Math.round(pos.coords.longitude * 100) / 100

  const now = new Date()
  const elevation = sunElevationDeg(now, lat, lon)

  // Clouds only matter while the sun is up; skip the network round-trip at night.
  let cloudCover = 0
  if (elevation > 0) {
    const res = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=cloud_cover,is_day`,
    )
    if (!res.ok) throw new Error(`weather ${res.status}`)
    const data = (await res.json()) as { current?: { cloud_cover?: number } }
    cloudCover = data.current?.cloud_cover ?? 0
  }

  return {
    sky: classifySky(elevation, cloudCover),
    elevationDeg: elevation,
    cloudCoverPct: cloudCover,
    at: now,
  }
}

/** True when the user has already granted location — lets us read silently. */
export async function locationPreGranted(): Promise<boolean> {
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' })
    return status.state === 'granted'
  } catch {
    return false
  }
}
